import { Button } from "@mui/material";
import Grid from "@mui/material/Grid";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { hasAnyAccessToResource, hasModifyAccessToResource } from "api/general";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useAccessToast } from "permissions/hooks/useAccessToast";
import React from "react";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";
import { checkPathsMatches, links } from "utilities/links";
import { ReactComponent as LogoWithText403 } from "./logoWithText403.svg";

interface PermissionRestrictedLayoutProps {
  children: React.ReactNode;
}

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    gridContainer: {
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    },
    title: {
      fontSize: "40px",
      color: theme.gray800,
      marginTop: "65px",
      marginBottom: "26px",
      fontWeight: 700,
    },
    subtitle: {
      fontSize: "18px",
      margin: 0,
      fontWeight: 400,
      color: theme.gray800,
      lineHeight: "150%",
    },
    button: {
      color: theme.primary500,
      marginTop: "26px",
      padding: theme.spacing(12),
      height: theme.spacing(24),
      width: "auto",
      border: `${theme.spacing(1)} solid ${theme.primary500} !important`,
      [theme.breakpoints.up("sm")]: { width: 274 },
      "&:hover": {
        color: theme.primary500,
        background: theme.white50,
      },
      fontWeight: 700,
      fontSize: "14px",
    },
  }),
);

export const PermissionRestrictedLayout = ({
  children,
}: PermissionRestrictedLayoutProps) => {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();

  const hasAnyAdminPermission = hasAnyAccessToResource("ADMIN");
  const hasModifyAdminPermission = hasModifyAccessToResource("ADMIN");
  const hasAnyCampaignPermission = hasAnyAccessToResource("CAMPAIGN");
  const hasModifyCampaignPermission = hasModifyAccessToResource("CAMPAIGN");
  const hasAnyTierPermission = hasAnyAccessToResource("LEVEL");
  const hasModifyTierPermission = hasModifyAccessToResource("LEVEL");
  const hasAnyTransactionPermission = hasAnyAccessToResource("TRANSACTION");
  const hasModifyCustomEventPermission =
    hasModifyAccessToResource("CUSTOM_EVENT");
  const hasAnyCustomEventPermission = hasAnyAccessToResource("CUSTOM_EVENT");
  const hasAnyChannelPermission = hasAnyAccessToResource("CHANNEL");
  const hasModifyChannelPermission = hasModifyAccessToResource("CHANNEL");
  const hasAnyRolePermission = hasAnyAccessToResource("ACL");
  const hasModifyRolePermission = hasModifyAccessToResource("ACL");
  const hasAnyLanguagePermission = hasAnyAccessToResource("LANGUAGE");
  const hasModifyLanguagePermission = hasModifyAccessToResource("LANGUAGE");
  const hasAnyImportPermission = hasAnyAccessToResource("IMPORT");
  const hasAnyExportPermission = hasAnyAccessToResource("EXPORT");
  const hasAnyWalletPermission = hasAnyAccessToResource("WALLET");
  const hasAnyRewardsPermission = hasAnyAccessToResource("REWARDS");
  const hasAnyCustomerPermission = hasAnyAccessToResource("CUSTOMER");
  const hasAnyBadgesPermission = hasAnyAccessToResource("BADGE");

  const location = useLocation();

  useAccessToast();

  const restrictedPages = [
    {
      path: links.transactionsList,
      allowCondition: hasAnyTransactionPermission,
    },
    {
      path: links.tiersList,
      allowCondition: hasAnyTierPermission,
    },
    {
      path: links.addTier,
      allowCondition: hasModifyTierPermission && hasAnyLanguagePermission,
    },
    {
      path: links.editTier,
      allowCondition: hasAnyTierPermission && hasAnyLanguagePermission,
    },
    {
      path: links.campaignsList,
      allowCondition: hasAnyCampaignPermission,
    },
    {
      path: links.addCampaign,
      allowCondition: hasModifyCampaignPermission && hasAnyLanguagePermission,
    },
    {
      path: links.editCampaign,
      allowCondition: hasAnyLanguagePermission,
    },
    {
      path: links.simulateCampaign,
      allowCondition: hasAnyCampaignPermission,
    },
    {
      path: links.addAdmin,
      allowCondition: hasModifyAdminPermission,
    },
    {
      path: links.editAdmin,
      allowCondition: hasAnyAdminPermission,
    },
    {
      path: links.adminData,
      allowCondition: hasAnyAdminPermission,
    },
    {
      path: links.customEventsList,
      allowCondition: hasAnyCustomEventPermission,
    },
    {
      path: links.customEvent,
      allowCondition: hasAnyCustomEventPermission,
    },
    {
      path: links.customEventsSchemaList,
      allowCondition: hasAnyCustomEventPermission,
    },
    {
      path: links.addCustomEventSchema,
      allowCondition: hasModifyCustomEventPermission,
    },
    {
      path: links.editCustomEventSchema,
      allowCondition: hasAnyCustomEventPermission,
    },
    {
      path: links.editChannel,
      allowCondition: hasAnyChannelPermission,
    },
    {
      path: links.addChannel,
      allowCondition: hasModifyChannelPermission,
    },
    {
      path: links.editRole,
      allowCondition: hasAnyRolePermission,
    },
    {
      path: links.addRole,
      allowCondition: hasModifyRolePermission,
    },
    {
      path: links.editTranslation,
      allowCondition: hasAnyLanguagePermission,
    },
    {
      path: links.addLanguage,
      allowCondition: hasModifyLanguagePermission,
    },
    {
      path: links.editLanguage,
      allowCondition: hasAnyLanguagePermission,
    },
    {
      path: links.achievementsList,
      allowCondition: hasAnyCampaignPermission,
    },
    {
      path: links.addAchievement,
      allowCondition: hasModifyCampaignPermission && hasAnyLanguagePermission,
    },
    {
      path: links.editAchievement,
      allowCondition: hasModifyCampaignPermission && hasAnyLanguagePermission,
    },
    {
      path: links.addReward,
      allowCondition: hasAnyLanguagePermission,
    },
    {
      path: links.editReward,
      allowCondition: hasAnyLanguagePermission,
    },
    {
      path: links.addWalletType,
      allowCondition: hasAnyLanguagePermission,
    },
    {
      path: links.editWalletType,
      allowCondition: hasAnyLanguagePermission,
    },
    {
      path: links.importsExports,
      allowCondition: hasAnyImportPermission || hasAnyExportPermission,
    },
    {
      path: links.singleMemberWallets(),
      allowCondition: hasAnyWalletPermission && hasAnyCustomerPermission,
    },
    {
      path: links.singleMemberTimeline(),
      allowCondition: hasAnyCustomerPermission,
    },
    {
      path: links.singleMemberTiers(),
      allowCondition: hasAnyTierPermission && hasAnyCustomerPermission,
    },
    {
      path: links.singleMemberTransactions(),
      allowCondition: hasAnyTransactionPermission && hasAnyCustomerPermission,
    },
    {
      path: links.singleMemberCustomEvents(),
      allowCondition: hasAnyCustomEventPermission && hasAnyCustomerPermission,
    },
    {
      path: links.singleMemberCampaigns(),
      allowCondition: hasAnyCampaignPermission && hasAnyCustomerPermission,
    },
    {
      path: links.singleMemberAchievements(),
      allowCondition: hasAnyCampaignPermission && hasAnyCustomerPermission,
    },
    {
      path: links.singleMemberRewards(),
      allowCondition: hasAnyRewardsPermission && hasAnyCustomerPermission,
    },
    {
      path: links.singleMemberReferrals(),
      allowCondition: hasAnyCustomerPermission,
    },
    {
      path: links.singleMemberGeneralInfo(),
      allowCondition: hasAnyCustomerPermission,
    },
    {
      path: links.singleMemberBadges(),
      allowCondition: hasAnyBadgesPermission,
    },
    {
      path: links.membersList,
      allowCondition: hasAnyCustomerPermission,
    },
  ];

  const findRestrictedPage = () => {
    return restrictedPages.find((item) =>
      checkPathsMatches(location.pathname, item.path),
    );
  };

  const foundRestrictedPage = findRestrictedPage();

  return foundRestrictedPage !== undefined ? (
    foundRestrictedPage.allowCondition ? (
      <>{children}</>
    ) : (
      <Grid className={classes.gridContainer}>
        <LogoWithText403 />
        <p className={classes.title}>{message({ id: "weAreSorry" })}</p>
        <p className={classes.subtitle}>
          {message({
            id: "noPermissionInfo",
          })}
        </p>
        <p className={classes.subtitle}>
          {message({
            id: "referToAdministratorInfo",
          })}
        </p>
        <Button
          variant="outlined"
          color="primary"
          className={classes.button}
          onClick={() => {
            navigate(-1);
          }}
        >
          {message({ id: "goBack" })}
        </Button>
      </Grid>
    )
  ) : (
    <>{children}</>
  );
};
